import { Form, message } from "antd";
import moment from "moment-timezone";
import { useEffect, useState } from "react";

import useValue from "../../../hooks/useValue";
import * as advanceBookingRequestService from "../../../services/advanceBookingRequestService";
import * as masterServices from "../../../services/masterServices";

import type { CheckSlotAvailabilityFormData, CheckSlotAvailabilityResponse, RaiseABRBookingData, RaiseABRFormData } from "../../../shared/interface/advanceBookingRequest";
import { useSelector } from "react-redux";
import { RootState } from "../../../appRedux/store";
import { CustomNotification } from "../../../util/CustomNotification";
import { PaymentGatewayConstant } from "../../../constants/DatabaseConstants/ABRConstants";
import FixMeLater from "../../../shared/types/fixMeLater.type";
import * as commonAPI from "../../../services/commonServices";
import { AdvanceBookingQuotaList } from "../../../shared/interface/MasterManagement/advanceBookingQuota.interface";

interface usePayAndParkProps {
  handleOnClose: () => void;
  handleRefreshList: () => void;
  selectedDate: string;
}

export const usePayAndPark = (props: usePayAndParkProps) => {
  /* CONSTANTS */
  const timePickerFormat = "HH:mm";
  const [form] = Form.useForm();
  enum Steps {
    CHECK_AVAILABLE_SLOTS,
    RAISE_ABR_REQUEST,
    RAISE_BOOKING_REQUEST
  }

  /* STATE */
  const [userDetails, setUserDetails] = useState<FixMeLater>(null);
  const [currentStep, setCurrentStep] = useState(Steps.CHECK_AVAILABLE_SLOTS);
  const [slotDetails, setSlotDetails] = useState<CheckSlotAvailabilityResponse["data"] | null>(null);
  const [bookingRequest, setBookingRequest] = useState<{
    id: number;
    booking_id: string;
    vehicle_type_id: number;
  } | null>(null);
  const [advanceBookingQuotaList, setAdvanceBookingQuotaList] = useState<AdvanceBookingQuotaList>([]);

  /* HELPER FUNCTIONS */
  const disabledRangeTime = () => {
    const now = moment();
    const selectedDate = moment(props.selectedDate);
    const currentHour = now.hour();
    const currentMinute = now.minute();

    if (!selectedDate.isSame(now, "day")) {
      return {
        disabledHours: () => [] as number[],
        disabledMinutes: () => [] as number[]
      };
    }

    const disabledHours = () => {
      return currentMinute >= 30 ? Array.from({ length: currentHour + 1 }, (_, i) => i) : Array.from({ length: currentHour }, (_, i) => i);
    };

    const disabledMinutes = (hour: number) => {
      if (hour !== currentHour) return [] as number[];
      return currentMinute < 30 ? [0] : [0, 30];
    };

    return { disabledHours, disabledMinutes };
  };

  const getFirstAvailableTime = () => {
    const date = moment(props.selectedDate);
    const { disabledHours, disabledMinutes } = disabledRangeTime();

    const disabledH = disabledHours();
    for (let h = 0; h < 24; h++) {
      if (!disabledH.includes(h)) {
        const disabledM = disabledMinutes(h);
        for (let m of [0, 30]) {
          if (!disabledM.includes(m)) {
            return date.clone().hour(h).minute(m).second(0);
          }
        }
      }
    }

    return date;
  };

  /* HOOKS */
  const { value: saving, handleDisableValue: handleDisableSaving, handleEnableValue: handleEnableSaving } = useValue(false);
  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);
  const { value: isValetParkingChecked, handleValue: handleValetParkingChecked } = useValue(false);
  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* HANDLERS */
  const handleCloseModal = () => {
    props.handleOnClose();
    if (currentStep !== Steps.CHECK_AVAILABLE_SLOTS) props.handleRefreshList();
  };

  const handleVerifyMobileNumber = async () => {
    try {
      const mobileNumber = "+91" + form.getFieldValue("mobile_number");
      const response = await commonAPI.verifyMobileUserAndGetDetails(mobileNumber);

      form.setFieldsValue({
        first_name: response?.data?.["first_name"],
        last_name: response?.data?.["last_name"]
      });
      setUserDetails(response?.data);
    } catch (error) {
      form.setFieldsValue({
        first_name: "",
        last_name: ""
      });
      setUserDetails(null);
      CustomNotification.showErrorMessage(error);
    }
  };

  const handleCurrentStep = (step: Steps) => {
    setCurrentStep(step);
  };

  const handleFinish = async (data: any) => {
    try {
      const customerLocationId = masterFilters?.locationId as number;
      const customerId = masterFilters?.customerId as number;
      if (!customerId || !customerLocationId) {
        CustomNotification.showErrorMessage("Please select customer and location.");
        return;
      }

      handleEnableSaving();

      if (currentStep === Steps.CHECK_AVAILABLE_SLOTS) {
        const formData = data as CheckSlotAvailabilityFormData;
        const selectedData = moment(props.selectedDate);

        const selectedFromDate = selectedData.clone().set("hour", formData.time.hour()).set("minute", formData.time.minute());
        const fromDate = selectedFromDate.format("YYYY-MM-DD HH:mm");
        const toDate = selectedFromDate.clone().add("hours", formData.hours).format("YYYY-MM-DD HH:mm");

        const vehicleTypeId = formData.vehicle_type_id;

        const slotData = {
          fromDate,
          toDate,
          vehicleTypeId,
          customerLocationId
        };

        const response = await advanceBookingRequestService.checkAvailableSlots(slotData);
        if (response.data.isSlotAvailable) {
          setSlotDetails(response.data);
          handleCurrentStep(Steps.RAISE_ABR_REQUEST);
          CustomNotification.showSuccessMessage("Parking Slot is available.");
        } else CustomNotification.showErrorMessage("No parking slot available.");
      } else if (currentStep === Steps.RAISE_ABR_REQUEST) {
        if (!slotDetails) return;

        const formData = data as RaiseABRFormData;

        const vehicleTypeId = formData.vehicle_type_id;

        const dataToPost = {
          parkingStartDate: slotDetails.fromDate,
          parkingEndDate: slotDetails.toDate,
          customerLocationId,
          vehicleTypeId,
          payableAmount: slotDetails.totalAmount,
          isValetParking: isValetParkingChecked,
          paymentGateway: PaymentGatewayConstant.OFFLINE,
          first_name: formData.first_name,
          last_name: formData.last_name,
          mobile_number: `+91${formData.mobile_number}`
        };

        const response = await advanceBookingRequestService.raiseAdvanceBookingRequest(dataToPost);
        setBookingRequest(response.data.advanceBookingRequest);

        handleCurrentStep(Steps.RAISE_BOOKING_REQUEST);

        CustomNotification.showSuccessMessage("Advance booking request created successfully.");
      } else if (currentStep === Steps.RAISE_BOOKING_REQUEST) {
        const formData = data as RaiseABRBookingData;
        if (!bookingRequest) return;

        const dataToPost = {
          customer_id: customerId,
          customer_location_id: customerLocationId,
          booking_id: formData.booking_id,
          key_fob_id: formData.key_fob_id,
          vehicle_number: formData.vehicle_number
        };

        const response = await advanceBookingRequestService.raiseParkingBookingRequestAPI(bookingRequest.id, dataToPost);
        props.handleOnClose();
        props.handleRefreshList();
        CustomNotification.showSuccessMessage(response?.message);
      }
    } catch (error: any) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableSaving();
    }
  };

  /* EFFECTS */
  useEffect(() => {
    const fetchAdvanceBookingQuotaList = async () => {
      handleEnableLoading();

      if (!masterFilters?.customerId || !masterFilters.locationId) {
        message.error("Please select customer and location");
        return;
      }

      return await masterServices.getAdvanceBookingQuotaList(masterFilters.customerId, masterFilters.locationId);
    };

    fetchAdvanceBookingQuotaList()
      .then((data) => {
        if (data) setAdvanceBookingQuotaList(data);
      })
      .catch((error: any) => {
        message.error(error.message);
      })
      .finally(() => {
        handleDisableLoading();
      });
  }, [masterFilters?.customerId, masterFilters?.locationId]);

  return {
    timePickerFormat,
    handleFinish,
    form,
    handleValetParkingChecked,
    advanceBookingQuotaList,
    loading,
    saving,
    Steps,
    currentStep,
    disabledRangeTime,
    isValetParkingChecked,
    slotDetails,
    bookingRequest,
    handleVerifyMobileNumber,
    userDetails,
    getFirstAvailableTime,
    handleCloseModal
  };
};
